import type { Camera } from '@babylonjs/core/Cameras/camera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { FONT, roundedRect } from '../tablets/tabletCanvas';
import { TILE_SIZE } from './constants';
import { createFloatingLabels, type SlotRect } from './floatingLabels';
import { OVERHEAD_GAP, OVERHEAD_GROW } from './overhead';

/** What a piece can be suffering, worst first: a piece under two shows only the heavier. */
export const AFFLICTIONS = ['imprisoned', 'frozen', 'cursed'] as const;
export type Affliction = (typeof AFFLICTIONS)[number];

const SIGN_TEXT: Record<Affliction, string> = {
  imprisoned: 'IMPRISONED',
  frozen: 'FROZEN',
  cursed: 'CURSED',
};

/** Ink and rim per affliction: iron for a cell, ice for a hold, a sick green for a curse. */
const SIGN_COLORS: Record<Affliction, { ink: string; rim: string }> = {
  imprisoned: { ink: '#e8ecf5', rim: '#9aa6bf' },
  frozen: { ink: '#dff3ff', rim: '#9fdcff' },
  cursed: { ink: '#e7f5d2', rim: '#8fbf5a' },
};

const ATLAS = { width: 1024, height: 512 };
const SLOT = { width: 256, height: 64 };
/** A sign is four times as wide as it is tall, and floats above the piece's head. */
const SIGN = { width: TILE_SIZE * 0.92, height: TILE_SIZE * 0.23 };
/**
 * From the top of the piece to the sign's centre, along the eye's up: its lower edge clears the
 * head by the gap, however tall the piece and from however high it is seen. A fixed lift of
 * 0.85 tiles over the feet put the sign across the head of every piece taller than that, which
 * is most of them (the owner's report of 2026-09-25, the Imprisoned sign).
 */
const SIGN_LIFT = OVERHEAD_GAP + SIGN.height / 2;
/** How far a sign reaches over its piece's top: a label over the same piece goes above it. */
export const SIGN_REACH = OVERHEAD_GAP + SIGN.height;
/** A piece's top until its body can be measured: about as tall as the pieces stand. */
export const DEFAULT_PIECE_TOP = TILE_SIZE * 0.95;

export interface PieceAffliction {
  pieceId: number;
  affliction: Affliction;
  /** Where the piece's feet are now; the sign floats above it and follows as it walks. */
  at: { x: number; y: number; z: number };
  /** How far the piece's top stands over its feet, when known. */
  top?: number;
}

export interface StatusSigns {
  /** Replaces the signs on show. */
  set(afflicted: readonly PieceAffliction[]): void;
  /** Keeps the signs over their pieces and facing the eye; call each frame. */
  update(
    camera: Camera | null,
    positionOf: (pieceId: number) => PieceAffliction['at'] | undefined,
    topOf?: (pieceId: number) => number | undefined,
  ): void;
  /** What the signs say, for tests. */
  texts(): string[];
  dispose(): void;
}

function drawSign(ctx: CanvasRenderingContext2D, rect: SlotRect, affliction: Affliction): void {
  const { ink, rim } = SIGN_COLORS[affliction];
  roundedRect(ctx, { x: rect.x + 6, y: rect.y + 8, w: rect.w - 12, h: rect.h - 16 }, 14);
  ctx.fillStyle = 'rgba(12, 9, 18, 0.82)';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = rim;
  ctx.stroke();
  const text = SIGN_TEXT[affliction];
  ctx.font = `700 26px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  // Read at a distance and over any colour of square: light letters, dark outline.
  ctx.lineWidth = 5;
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
  ctx.strokeText(text, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1);
  ctx.fillStyle = ink;
  ctx.fillText(text, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1);
}

/**
 * The signs over afflicted pieces: IMPRISONED, FROZEN, CURSED.
 *
 * A spell that does nothing you can see is a spell nobody casts, and all three of these work on a
 * piece that carries on looking exactly as it did. The sign is the whole of the feedback, so it
 * always faces the viewer however the table is turned, and it follows its piece rather than its
 * square, so a cursed piece walking to a new square keeps its sign overhead the whole way.
 *
 * One draw call for all of them, whatever the board holds (see `floatingLabels.ts`).
 */
export function createStatusSigns(scene: Scene, parent: TransformNode | null = null): StatusSigns {
  const sheet = createFloatingLabels<Affliction>(scene, {
    name: 'statusSigns',
    parent,
    atlas: ATLAS,
    slot: SLOT,
    size: SIGN,
    draw: drawSign,
    grow: OVERHEAD_GROW,
  });
  let showing: PieceAffliction[] = [];
  /** The tops of the afflicted pieces, which their signs stand over. */
  const heads: Vector3[] = [];

  const headOf = (at: PieceAffliction['at'], top: number | undefined, out = new Vector3()) =>
    out.set(at.x, at.y + (top ?? DEFAULT_PIECE_TOP), at.z);

  return {
    set(afflicted) {
      showing = [...afflicted];
      heads.length = 0;
      for (const a of showing) heads.push(headOf(a.at, a.top));
      sheet.set(showing.map((a, i) => ({ at: heads[i]!, item: a.affliction, lift: SIGN_LIFT })));
    },
    update(camera, positionOf, topOf) {
      if (showing.length === 0) return;
      // Follow the pieces: a sign belongs over a head, not over a square it has left. The top is
      // asked again too, for a model that came in after its placeholder.
      showing.forEach((a, i) => {
        const at = positionOf(a.pieceId);
        if (at) headOf(at, topOf?.(a.pieceId) ?? a.top, heads[i]);
      });
      sheet.moveTo(heads);
      if (camera) sheet.face(camera);
    },
    texts() {
      return showing.map((a) => SIGN_TEXT[a.affliction]);
    },
    dispose() {
      sheet.dispose();
    },
  };
}

/** The worst affliction a piece is under, or `null` when it is free. */
export function afflictionOf(piece: {
  imprisoned: boolean;
  frozen: number;
  cursed: number;
}): Affliction | null {
  if (piece.imprisoned) return 'imprisoned';
  if (piece.frozen > 0) return 'frozen';
  if (piece.cursed > 0) return 'cursed';
  return null;
}
